import React, { useCallback, useContext, useState } from 'react'
import { useFocusEffect } from '@react-navigation/native'
import { View, StyleSheet, ScrollView } from 'react-native'
import { Text, Button, Modal, Spinner, useTheme } from '@ui-kitten/components'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import * as Sentry from '@sentry/react-native'
import { UserContext } from '../../global/UserProvider'
import { productsDictAtom, storesDictAtom, suppliersDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { apiDraftsApi } from '../../global/ApiTokens/apiDraftsApi'
import { apiTokensErrorCode } from '../../global/ApiTokens/apiTokensApi'
import { deviceDraftReports } from '../../global/ApiTokens/apiDraftReports'
import {
  acceptanceDraftPreselectedItems,
  apiDraftRefusalKey,
  draftApplicability,
} from '../../global/ApiTokens/apiDraftsView'

/**
 * ATL-1445 — Settings → Agent drafts. The sales and acceptances an AI agent proposed through Atlas
 * API v1. Nothing here saves a sale or an acceptance: "Open" hands the draft to the ordinary New
 * Sale / acceptance screen, where the merchant checks it and saves it with that screen's own button
 * and writer; the backend then verifies the saved record in Firestore before marking the draft
 * applied. "Dismiss" declines it. The list shows only the kinds this person's role may act on.
 */
export default function AgentDraftsScreen({ navigation }) {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const [context] = useContext(UserContext)
  const productsDict = useAtomValue(productsDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const [drafts, setDrafts] = useState(null)
  const [savedHere, setSavedHere] = useState({})
  const [loadError, setLoadError] = useState(null)
  const [dismissTarget, setDismissTarget] = useState(null)
  const [dismissing, setDismissing] = useState(false)
  const [dismissError, setDismissError] = useState(null)

  const canUseOtherStores = context.user?.role === 'admin' || userRole?.permissions?.viewOtherStores === true
  const formatDate = (value) => (value ? format(new Date(value), 'dd.MM.yyyy HH:mm') : '')
  const errorText = (error) => {
    const code = apiTokensErrorCode(error)
    if (code && i18n.exists(`apiDrafts.error_${code}`)) return t(`apiDrafts.error_${code}`)
    return t('apiDrafts.error_generic')
  }

  const organizationId = context.user?.organizationId
  const load = useCallback(async () => {
    setLoadError(null)
    // Reports first: a draft whose sale this device saved while offline should arrive here as
    // applied, not as an open draft that invites a second sale.
    try {
      await deviceDraftReports.retryPending(organizationId)
    } catch (error) {
      Sentry.captureException(error, { level: 'warning', tags: { feature: 'api-draft' } })
    }
    try {
      const [list, pending] = await Promise.all([apiDraftsApi.list(), deviceDraftReports.pending(organizationId)])
      setDrafts(list)
      setSavedHere(pending)
    } catch (error) {
      Sentry.captureException(error)
      setLoadError(errorText(error))
    }
    // errorText reads t/i18n, which change identity only when the language does.
  }, [t, i18n, organizationId])

  // On every focus, not just mount: coming back from New Sale / the acceptance form is exactly when
  // a draft has just been saved.
  useFocusEffect(useCallback(() => {
    void load()
  }, [load]))

  const open = (draft) => {
    if (draft.kind === 'sale') {
      navigation.navigate('NewSale', { apiSaleDraft: draft })
    } else {
      navigation.navigate('SupplyDetail', {
        preselectedItems: acceptanceDraftPreselectedItems(draft, productsDict),
        preselectedStore: draft.store.id,
        apiAcceptanceDraft: draft,
      })
    }
  }

  const confirmDismiss = async () => {
    if (!dismissTarget) return
    setDismissing(true)
    setDismissError(null)
    try {
      await apiDraftsApi.dismiss(dismissTarget.id)
      // A refused record's note has done its job once the draft is decided.
      await deviceDraftReports.forget(dismissTarget.id)
      setDismissTarget(null)
      await load()
    } catch (error) {
      Sentry.captureException(error)
      setDismissError(errorText(error))
    } finally {
      setDismissing(false)
    }
  }

  const statusLine = (verdict) => {
    switch (verdict.reason) {
      case 'saved_here':
        return verdict.refusedCode !== undefined
          ? { status: 'danger', text: t('apiDrafts.savedRefused', { reason: t(apiDraftRefusalKey(verdict.refusedCode)) }) }
          : { status: 'info', text: t('apiDrafts.savedConfirming') }
      case 'other_store':
        return { status: 'warning', text: t('apiDrafts.otherStore') }
      case 'store_missing':
        return { status: 'warning', text: t('apiDrafts.storeMissing') }
      case 'supplier_mismatch':
        return { status: 'warning', text: t('apiDrafts.supplierMismatch') }
      case 'products_missing':
        return { status: 'warning', text: t('apiDrafts.productsMissing', { names: verdict.missing.join(', ') }) }
      default:
        return null
    }
  }

  const card = [styles.card, { backgroundColor: theme['background-basic-color-1'] }]

  if (!drafts && !loadError) {
    return (
      <View style={styles.center}>
        <Spinner />
      </View>
    )
  }

  return (
    <ScrollView style={styles.container} testID='agent-drafts-screen'>
      <View style={card}>
        <Text category='s1' style={styles.gap}>{t('apiDrafts.title')}</Text>
        <Text appearance='hint'>{t('apiDrafts.intro')}</Text>
        {loadError && <Text status='danger' style={styles.topGap}>{loadError}</Text>}
      </View>

      {drafts?.length === 0 && (
        <View style={card}>
          <Text appearance='hint'>{t('apiDrafts.empty')}</Text>
        </View>
      )}

      {drafts?.map((draft) => {
        const verdict = draftApplicability(draft, {
          productsDict,
          storesDict,
          suppliersDict,
          currentStoreId: context.user?.store?.id,
          canUseOtherStores,
          savedHere: savedHere[draft.id],
        })
        const line = verdict.ok ? null : statusLine(verdict)
        return (
          <View key={draft.id} style={card} testID={`agent-draft-${draft.id}`}>
            <Text category='s2'>
              {t(draft.kind === 'sale' ? 'apiDrafts.kind_sale' : 'apiDrafts.kind_acceptance')}
              {' · '}
              {draft.store.name ?? storesDict[draft.store.id]?.name ?? draft.store.id}
            </Text>
            <Text category='c1' appearance='hint'>
              {t('apiDrafts.proposedBy', {
                name: draft.proposedBy.tokenName ?? t('apiDrafts.agent'),
                person: draft.proposedBy.name ?? '—',
                date: formatDate(draft.createdAt),
              })}
            </Text>
            <Text category='c1' appearance='hint' style={styles.gap}>
              {t('apiDrafts.expires', { date: formatDate(draft.expiresAt) })}
            </Text>
            {draft.note ? <Text category='p2' style={styles.gap}>{draft.note}</Text> : null}
            {draft.items.map((item) => (
              <Text key={item.productId} category='p2'>
                {`${productsDict[item.productId]?.name ?? item.name ?? item.productId} × ${item.quantity}`}
                {item.purchasePrice !== undefined ? ` · ${t('apiDrafts.purchasePrice', { price: item.purchasePrice })}` : ''}
              </Text>
            ))}
            {line && <Text status={line.status} category='p2' style={styles.topGap}>{line.text}</Text>}
            <View style={styles.row}>
              {verdict.ok && (
                <Button size='small' onPress={() => open(draft)} style={styles.rowButton} testID={`agent-draft-open-${draft.id}`}>
                  {t(draft.kind === 'sale' ? 'apiDrafts.openSale' : 'apiDrafts.openAcceptance')}
                </Button>
              )}
              {!(verdict.reason === 'saved_here' && verdict.refusedCode === undefined) && (
                <Button
                  size='small'
                  status='basic'
                  appearance='outline'
                  onPress={() => {
                    setDismissError(null)
                    setDismissTarget(draft)
                  }}
                  testID={`agent-draft-dismiss-${draft.id}`}
                >
                  {t('apiDrafts.dismiss')}
                </Button>
              )}
            </View>
          </View>
        )
      })}

      <Modal
        visible={dismissTarget !== null}
        backdropStyle={styles.backdrop}
        onBackdropPress={() => !dismissing && setDismissTarget(null)}
        style={styles.modal}
      >
        <View style={[styles.modalContent, { backgroundColor: theme['background-basic-color-1'] }]}>
          <Text category='h6' style={styles.gap}>{t('apiDrafts.dismissTitle')}</Text>
          <Text style={styles.gap}>{t('apiDrafts.dismissText')}</Text>
          {dismissError && <Text status='danger' style={styles.gap}>{dismissError}</Text>}
          <View style={styles.row}>
            <Button status='basic' onPress={() => setDismissTarget(null)} disabled={dismissing} style={styles.rowButton}>
              {t('apiDrafts.cancel')}
            </Button>
            <Button status='danger' onPress={confirmDismiss} disabled={dismissing} testID='agent-draft-dismiss-confirm'>
              {t('apiDrafts.dismiss')}
            </Button>
          </View>
        </View>
      </Modal>
      <View style={{ height: 50 }} />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  card: { borderRadius: 10, padding: 15, marginTop: 10 },
  gap: { marginBottom: 8 },
  topGap: { marginTop: 8 },
  row: { flexDirection: 'row', marginTop: 12 },
  rowButton: { marginRight: 10 },
  backdrop: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
  modal: { width: '90%', maxWidth: 420 },
  modalContent: { borderRadius: 10, padding: 20 },
})
